import { useParams } from "react-router";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { useFetchInvoiceQuery } from "../services/api/invoice";

import { useSession } from "./useSession";

/** A bank debit or saved card charge is still processing, so the invoice can change without the customer doing anything */
const PAYMENT_IN_PROGRESS_POLL_MS = 10 * 1000;

export function usePortalInvoice() {
  const session = useSession();

  const { code } = useParams<{ code: string}>();
  // On the second request we want to sync with accounting system
  const [resync, setResync] = useState(false);
  const [polling, setPolling] = useState(false);
  // Polls are plain reads of our own records (no accounting resync); the switch back to resync when the
  // payment stops processing is itself one resync, which picks up the paid invoice
  const api = useFetchInvoiceQuery({ code, resync: resync && !polling, sessionId: session?.id }, {
    refetchOnMountOrArgChange: true,
    skip: !code || !window.location.pathname.includes(`/i/`) || !session?.id,
    pollingInterval: polling ? PAYMENT_IN_PROGRESS_POLL_MS : 0,
    skipPollingIfUnfocused: true,
  });

  // trigger a second request with resync flag on render
  useEffect(() => {
    if (!resync && api.data && !api.data?.requiresVerification) {
      setResync(true);
    }
  }, [api.data]);

  useEffect(() => {
    const data = api.data;

    setPolling(!!data && !data.requiresVerification && data.invoice?.xero_status === `AUTHORISED` && (data.processingStripePayments?.length ?? 0) > 0);
  }, [api.data]);

  useEffect(() => {
    // The header spinner covers polls; the toast is for the one-off resync after first load
    if (resync && !polling) {
      // Show indicator when fetching
      if (api.isFetching) {
        toast.loading(`Updating invoice...`, { autoClose: false, toastId: `loading` });
      }

      if (!api.isFetching) {
        toast.dismiss(`loading`);
      }
    }
  }, [api.isFetching, resync, polling]);

  return api;
}
